import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiClock, FiCheck, FiX, FiEye, FiBarChart3, FiFilter, FiUpload, FiSquare, FiCheckSquare, FiSettings, FiChevronDown, FiChevronUp, FiAlertCircle, FiLayers, FiMinusSquare } = FiIcons;

export default function Dashboard() {
  const { getFilteredProviders, files, terminMarktData, spotMarktData, hasFile, renderValue, getAllProviders, selectedColumns, columnDisplayNames, updateSelectedColumn, selectAllColumns, clearAllColumns, hasColumnSelectionChanges, getGlobalDataStatus } = useData();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [marktart, setMarktart] = useState('terminmarkt');
  const [energieart, setEnergieart] = useState('strom');
  
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  
  useEffect(() => {
    const data = getFilteredProviders(marktart, energieart);
    setFilteredProviders(data);
    setSelectedProviders([]);
    setAreAllSelected(false);
  }, [marktart, energieart, getFilteredProviders]);

  useEffect(() => {
    if (filteredProviders.length > 0 && selectedProviders.length === filteredProviders.length) {
      setAreAllSelected(true);
    } else {
      setAreAllSelected(false);
    }
  }, [selectedProviders, filteredProviders]);

  const handleProviderSelect = (providerId) => {
    setSelectedProviders(prev => prev.includes(providerId) ? prev.filter(id => id !== providerId) : [...prev, providerId]);
  };

  const handleSelectAll = () => setSelectedProviders(filteredProviders.map(p => p.id));
  const handleDeselectAll = () => setSelectedProviders([]);
  const handleCompare = () => {
    if (selectedProviders.length > 0) navigate(`/comparison?ids=${selectedProviders.join(',')}&markt=${marktart}&energie=${energieart}`);
  };

  const getDataStatusDate = () => {
    const dates = [];
    if (hasFile('terminMarkt')) dates.push(new Date(files.terminMarkt.uploadDate));
    if (hasFile('spotMarkt')) dates.push(new Date(files.spotMarkt.uploadDate));
    return dates.length > 0 ? new Date(Math.max(...dates)) : null;
  };
  const lastUpdated = getDataStatusDate();
  const overview = { terminmarkt: terminMarktData.length, spotmarkt: spotMarktData.length, total: terminMarktData.length + spotMarktData.length };
  const hasAnyData = overview.total > 0;
  const isAdmin = user?.role === 'admin';

  const getTableColumns = () => {
    const baseColumns = [
      { key: 'selection', label: 'Auswahl', alwaysShow: true },
      { key: 'evu', label: 'EVU', alwaysShow: true }
    ];

    const dynamicColumns = [];
    
    if (energieart === 'strom' || energieart === 'alle') {
      if (selectedColumns.strom_slp) dynamicColumns.push({ key: 'strom_slp', label: 'Strom SLP' });
      if (selectedColumns.strom_rlm) dynamicColumns.push({ key: 'strom_rlm', label: 'Strom RLM' });
      if (selectedColumns.verbrauchsgrenzen_strom) dynamicColumns.push({ key: 'verbrauchsgrenzen_strom', label: energieart === 'alle' ? 'Verbrauchsgrenzen Strom' : 'Verbrauchsgrenzen' });
    }

    if (energieart === 'gas' || energieart === 'alle') {
      if (selectedColumns.gas_slp) dynamicColumns.push({ key: 'gas_slp', label: 'Gas SLP' });
      if (selectedColumns.gas_rlm) dynamicColumns.push({ key: 'gas_rlm', label: 'Gas RLM' });
      if (selectedColumns.verbrauchsgrenzen_gas) dynamicColumns.push({ key: 'verbrauchsgrenzen_gas', label: energieart === 'alle' ? 'Verbrauchsgrenzen Gas' : 'Verbrauchsgrenzen' });
    }

    if (selectedColumns.mehr_mindermengen_in_prozent) dynamicColumns.push({ key: 'mehr_mindermengen_in_prozent', label: 'Mehr-/Mindermengen in %' });
    
    if (selectedColumns.provisionsgrenze) dynamicColumns.push({ key: 'provisionsgrenze', label: 'Provisionsgrenze' });
    if (selectedColumns.auszahlung) dynamicColumns.push({ key: 'auszahlung', label: 'Auszahlung' });
    if (selectedColumns.buendelung) dynamicColumns.push({ key: 'buendelung', label: 'Bündelung' });
    if (selectedColumns.kuendigungsfrist) dynamicColumns.push({ key: 'kuendigungsfrist', label: 'Kündigungsfrist' });
    if (selectedColumns.verlaengerung_aktiv_passiv) dynamicColumns.push({ key: 'verlaengerung_aktiv_passiv', label: 'Verlängerung' });
    
    dynamicColumns.push({ key: 'actions', label: 'Aktionen', alwaysShow: true });
    return [...baseColumns, ...dynamicColumns];
  };

  const renderDataRow = (provider) => {
    const isSelected = selectedProviders.includes(provider.id);
    const columns = getTableColumns();
    
    return (
      <motion.tr key={provider.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-gray-50 transition-colors">
        {columns.map(column => (
          <td key={column.key} className={`px-6 py-4 whitespace-nowrap ${column.key === 'evu' ? 'font-medium' : ''}`}>
            {column.key === 'selection' && (
              <input type="checkbox" checked={isSelected} onChange={() => handleProviderSelect(provider.id)} className="w-5 h-5 text-[#9b061b] border-gray-300 rounded focus:ring-[#9b061b] cursor-pointer" />
            )}
            {column.key === 'evu' && (
              <div>
                <div className="text-sm font-medium text-gray-900">{renderValue(provider.evu)}</div>
                <div className="text-xs text-gray-500">{provider.bietet_strom && 'Strom'} {provider.bietet_strom && provider.bietet_gas && '+'} {provider.bietet_gas && 'Gas'}</div>
              </div>
            )}
            {column.key !== 'selection' && column.key !== 'evu' && column.key !== 'actions' && (
              <div className="text-sm text-gray-900">
                {renderValue(provider[column.key])}
              </div>
            )}
            {column.key === 'actions' && (
              <Link to={`/details/${provider.id}?markt=${marktart}&energie=${energieart}`} className="inline-flex items-center px-3 py-1 border border-[#1e314b] text-[#1e314b] rounded-lg hover:bg-[#1e314b] hover:text-white transition-colors text-sm">
                <SafeIcon icon={FiEye} className="mr-1" />Details
              </Link>
            )}
          </td>
        ))}
      </motion.tr>
    );
  };

  const getDisplayableColumnOptions = () => {
    return Object.entries(columnDisplayNames);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#1e314b]">Datenübersicht</h2>
          <button onClick={() => setShowColumnSelector(!showColumnSelector)} className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors relative">
            <SafeIcon icon={FiSettings} className="mr-2" />
            Spalten anpassen
            <SafeIcon icon={showColumnSelector ? FiChevronUp : FiChevronDown} className="ml-2" />
            {hasColumnSelectionChanges() && (
              <span className="absolute top-[-2px] right-[-2px] flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
          </button>
        </div>
        
        {showColumnSelector && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">Anzuzeigende Spalten</h3>
              <div className="space-x-2">
                <button onClick={selectAllColumns} className="text-xs px-2 py-1 bg-[#1e314b] text-white rounded hover:bg-[#152738] transition-colors">Alle auswählen</button>
                <button onClick={clearAllColumns} className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors">Alle entfernen</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {getDisplayableColumnOptions().map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-white p-2 rounded">
                  <input type="checkbox" checked={selectedColumns[key]} onChange={(e) => updateSelectedColumn(key, e.target.checked)} className="w-4 h-4 text-[#9b061b] border-gray-300 rounded focus:ring-[#9b061b]" />
                  <span className={selectedColumns[key] ? 'font-medium' : 'text-gray-600'}>{label}</span>
                </label>
              ))}
            </div>
          </motion.div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2"><SafeIcon icon={FiTrendingUp} className="text-blue-500" /><span className="font-medium text-blue-800">Terminmarkt</span></div>
            <p className="text-2xl font-bold text-blue-900">{overview.terminmarkt}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2"><SafeIcon icon={FiTrendingUp} className="text-green-500" /><span className="font-medium text-green-800">Spotmarkt</span></div>
            <p className="text-2xl font-bold text-green-900">{overview.spotmarkt}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2"><SafeIcon icon={FiBarChart3} className="text-purple-500" /><span className="font-medium text-purple-800">Gesamt</span></div>
            <p className="text-2xl font-bold text-purple-900">{overview.total}</p>
          </div>
        </div>
      </div>

      {!hasAnyData && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
          <SafeIcon icon={FiUpload} className="text-yellow-500 text-4xl mb-4 mx-auto" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">Keine Daten vorhanden</h3>
          <p className="text-yellow-700 mb-4">{isAdmin ? 'Bitte laden Sie Daten im Admin-Panel hoch.' : 'Bitte kontaktieren Sie Ihren Administrator.'}</p>
          {isAdmin && <Link to="/admin" className="inline-flex items-center px-4 py-2 bg-[#9b061b] text-white rounded-lg hover:bg-[#7a0516] transition-colors"><SafeIcon icon={FiUpload} className="mr-2" />Zum Admin-Panel</Link>}
        </motion.div>
      )}

      {hasAnyData && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-lg font-semibold text-[#1e314b]">Filter & Einstellungen</h3>
            {lastUpdated && <div className="flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full border border-gray-200"><SafeIcon icon={FiClock} /><span>Stand: {lastUpdated.toLocaleDateString('de-DE')}</span></div>}
          </div>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Marktart wählen</label>
              <div className="flex space-x-4">
                {[{ value: 'terminmarkt', label: 'Terminmarkt' }, { value: 'spotmarkt', label: 'Spotmarkt' }].map((option) => (
                  <motion.button key={option.value} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setMarktart(option.value)} className={`flex items-center space-x-2 px-6 py-3 rounded-lg border-2 transition-all ${marktart === option.value ? 'border-[#1e314b] bg-[#1e314b] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#9b061b]'}`}><SafeIcon icon={FiTrendingUp} /><span className="font-medium">{option.label}</span></motion.button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Energieart wählen</label>
              <div className="flex space-x-4">
                {[{ value: 'alle', label: 'Alle', icon: FiLayers }, { value: 'strom', label: 'Strom', icon: FiTrendingUp }, { value: 'gas', label: 'Gas', icon: FiTrendingUp }].map((option) => (
                  <motion.button key={option.value} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setEnergieart(option.value)} className={`flex items-center space-x-2 px-6 py-3 rounded-lg border-2 transition-all ${energieart === option.value ? 'border-[#9b061b] bg-[#9b061b] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#9b061b]'}`}><SafeIcon icon={option.icon} /><span className="font-medium">{option.label}</span></motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {hasAnyData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#1e314b]">Gefundene Versorger: {filteredProviders.length}</h3>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={handleSelectAll} disabled={areAllSelected && selectedProviders.length > 0} className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm transition-colors ${areAllSelected && selectedProviders.length > 0 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}><SafeIcon icon={FiCheckSquare} className="mr-2" />Alle auswählen</button>
                <button onClick={handleDeselectAll} disabled={selectedProviders.length === 0} className={`inline-flex items-center px-3 py-2 border rounded-lg text-sm transition-colors ${selectedProviders.length === 0 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}><SafeIcon icon={FiMinusSquare} className="mr-2" />Auswahl aufheben</button>
                {selectedProviders.length > 0 && (
                  <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handleCompare} className="bg-[#9b061b] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#7a0516] transition-colors flex items-center shadow-sm ml-2"><SafeIcon icon={FiBarChart3} className="mr-2" />{selectedProviders.length} Vergleichen</motion.button>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {getTableColumns().map(column => (
                    <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{column.key === 'selection' ? <div className="flex items-center"><span className="sr-only">Auswahl</span><SafeIcon icon={FiCheckSquare} className="text-gray-400" /></div> : column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">{filteredProviders.map(renderDataRow)}</tbody>
            </table>
          </div>
          {filteredProviders.length === 0 && (
            <div className="text-center py-12"><SafeIcon icon={FiTrendingUp} className="text-gray-400 text-4xl mb-4 mx-auto" /><p className="text-gray-500">Keine Versorger gefunden.</p></div>
          )}
        </div>
      )}
    </div>
  );
}